import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { tapHaptic } from '../../shared/lib/haptics';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PageHeader } from '../../shared/ui/PageHeader';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { useToast } from '../../shared/ui/toast-context';

import {
  ADMIN_QUERY_KEYS,
  createInstitute,
  getDefaultInstitute,
  listInstitutes,
  provisionUser,
  setDefaultInstitute,
  type InstituteRow,
} from './api';
import s from './InstitutesPage.module.css';
import { createInstituteSchema, type CreateInstituteInput } from './schemas';

function initials(value: string): string {
  const parts = value.split(/[@\s.-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

const SKELETON_ROWS = [0, 1, 2, 3, 4] as const;

/** Mirrors `.row` — avatar, two lines of text, a status pill and the Manage link. */
function InstituteRowSkeleton(): JSX.Element {
  return (
    <li className={s.row}>
      <Skeleton variant="circle" width={40} height={40} />
      <div className={s.rowMain}>
        <Skeleton width="45%" height={14} />
        <Skeleton width="65%" height={12} />
      </div>
      <Skeleton width={56} height={18} radius="xl" />
      <Skeleton width={64} height={32} radius="md" />
    </li>
  );
}

export function AdminInstitutesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ADMIN_QUERY_KEYS.institutes,
    queryFn: listInstitutes,
  });

  // Phase 126 — where a self-serve sign-up (email or Google) gets enrolled.
  // Until one is chosen, ensure_default_enrollment returns 'not_configured' and
  // every new account lands on the "we couldn't place your account" screen, so
  // an unset default is surfaced as a warning rather than a quiet blank.
  const defaultInstitute = useQuery({
    queryKey: ADMIN_QUERY_KEYS.defaultInstitute,
    queryFn: getDefaultInstitute,
  });

  const makeDefault = useMutation({
    mutationFn: setDefaultInstitute,
    onSuccess: (row) => {
      setServerError(null);
      toast.show(`New sign-ups will now be enrolled into "${row.name}".`, { tone: 'success' });
      tapHaptic();
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.defaultInstitute });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const create = useMutation({
    mutationFn: async (values: CreateInstituteInput) => {
      let provisioned = false;
      if (values.owner_password !== '') {
        await provisionUser({ email: values.owner_email, password: values.owner_password });
        provisioned = true;
      }
      const row = await createInstitute({
        name: values.name,
        slug: values.slug,
        owner_email: values.owner_email,
      });
      return { row, provisioned };
    },
    onSuccess: ({ row, provisioned }: { row: InstituteRow; provisioned: boolean }) => {
      toast.show(
        provisioned
          ? `Created "${row.name}". Owner can log in with the password you set.`
          : `Created "${row.name}". Owner needs to log in (or sign up first if they haven't).`,
        { tone: 'success' },
      );
      tapHaptic();
      reset();
      void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.institutes });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateInstituteInput>({
    resolver: zodResolver(createInstituteSchema),
    defaultValues: { name: '', slug: '', owner_email: '', owner_password: '' },
  });

  async function onSubmit(values: CreateInstituteInput): Promise<void> {
    setServerError(null);
    try {
      await create.mutateAsync(values);
    } catch {
      /* onError set the serverError message */
    }
  }

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Tenancy"
        title="Institutes"
        subtitle={
          <>
            Create institutes and assign owners. Owners and members must already have signed up; ask
            them to sign up at <code>/signup</code> first.
          </>
        }
      />

      {defaultInstitute.isPending ? null : defaultInstitute.data ? (
        <StatusBanner tone="info" className={s.defaultBanner ?? ''}>
          New sign-ups are enrolled as students of <strong>{defaultInstitute.data.name}</strong> (
          <code>{defaultInstitute.data.slug}</code>).
        </StatusBanner>
      ) : (
        <StatusBanner tone="warning" className={s.defaultBanner ?? ''}>
          <strong>No default institute is set.</strong> Anyone who signs up with email or Google
          cannot be enrolled and will see a &ldquo;we couldn&rsquo;t place your account&rdquo;
          screen. Pick one below with <em>Make default</em>.
        </StatusBanner>
      )}

      <section className={s.section}>
        <div className={s.sectionHead}>
          <span className={s.sectionIcon} aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 14V6.5L8 2l6 4.5V14M2 14h12M6 14V9.5h4V14" />
            </svg>
          </span>
          <h2 className={s.sectionTitle}>Create institute</h2>
        </div>

        <form
          noValidate
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className={s.form}
        >
          <div className={s.formFull}>
            <Label htmlFor="name">Institute name</Label>
            <Input
              id="name"
              autoComplete="organization"
              placeholder="Acme Coaching"
              invalid={errors.name !== undefined}
              aria-describedby={errors.name ? 'name-error' : undefined}
              {...register('name')}
            />
            <FormError id="name-error" message={errors.name?.message} />
          </div>
          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              autoComplete="off"
              placeholder="my-institute"
              invalid={errors.slug !== undefined}
              aria-describedby={errors.slug ? 'slug-error' : undefined}
              {...register('slug')}
            />
            <FormError id="slug-error" message={errors.slug?.message} />
          </div>
          <div>
            <Label htmlFor="owner_email">Owner email</Label>
            <Input
              id="owner_email"
              type="email"
              autoComplete="off"
              placeholder="owner@institute.com"
              invalid={errors.owner_email !== undefined}
              aria-describedby={errors.owner_email ? 'owner_email-error' : undefined}
              {...register('owner_email')}
            />
            <FormError id="owner_email-error" message={errors.owner_email?.message} />
          </div>
          <div className={s.formFull}>
            <Label htmlFor="owner_password">Owner password (optional)</Label>
            <Input
              id="owner_password"
              type="password"
              autoComplete="new-password"
              placeholder="Leave blank if the owner has already signed up"
              invalid={errors.owner_password !== undefined}
              aria-describedby={
                errors.owner_password ? 'owner_password-error' : 'owner_password-hint'
              }
              {...register('owner_password')}
            />
            <FormError id="owner_password-error" message={errors.owner_password?.message} />
            <p id="owner_password-hint" className={s.hint}>
              If set, an account is created (or password reset) for this email and OTP is skipped —
              owner can log in directly. Minimum 8 characters.
            </p>
          </div>

          {serverError !== null ? (
            <div className={s.formFull}>
              <StatusBanner tone="error">{serverError}</StatusBanner>
            </div>
          ) : null}

          <div className={`${s.formFull} ${s.formActions}`}>
            <Button type="submit" loading={isSubmitting || create.isPending}>
              Create institute
            </Button>
          </div>
        </form>
      </section>

      <section className={s.subSection}>
        <header className={s.subSectionHead}>
          <h2 className={s.subSectionTitle}>All institutes</h2>
          <span className={s.subSectionMeta}>{(list.data ?? []).length} total</span>
        </header>
        {list.isPending ? (
          <ul className={s.list} role="status" aria-label="Loading institutes">
            {SKELETON_ROWS.map((row) => (
              <InstituteRowSkeleton key={row} />
            ))}
          </ul>
        ) : list.error !== null ? (
          <StatusBanner tone="error">Could not load institutes: {list.error.message}</StatusBanner>
        ) : (list.data ?? []).length === 0 ? (
          <p className={s.empty}>No institutes yet. Use the form above.</p>
        ) : (
          <ul className={s.list}>
            {list.data?.map((inst) => (
              <li key={inst.id} className={s.row}>
                <span className={s.avatar} aria-hidden="true">
                  {initials(inst.name)}
                </span>
                <div className={s.rowMain}>
                  <span className={s.rowName}>{inst.name}</span>
                  <span className={s.rowMeta}>
                    <code>{inst.slug}</code> · {inst.owner_email}
                  </span>
                </div>
                <span className={s.badge}>{inst.status}</span>
                {defaultInstitute.data?.institute_id === inst.id ? (
                  <span className={s.defaultBadge}>Default</span>
                ) : inst.status === 'active' ? (
                  <button
                    type="button"
                    className={s.makeDefault}
                    disabled={makeDefault.isPending}
                    onClick={() => makeDefault.mutate(inst.id)}
                  >
                    Make default
                  </button>
                ) : null}
                <Link to={`/admin/institutes/${inst.id}`} className={s.manage}>
                  Manage
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
